import type {
  GraphEdgeSettings,
  GroupEdgeSettings,
  ProtocolEdgeType,
  TopologyGraphEdgeSettings,
} from '@ipfabric/utils';
import type { FormSettingsValues } from 'components/Graph/GraphSidePanel/GraphProtocolsSettingsWindow/GraphProtocolsSettings';
import { isGroupSettings } from 'components/Graph/GraphState';
import { updateGraphDataFromSettings } from 'components/Graph/commands/updateGraphDataFromSettings';
import type { GraphCommandFactory } from 'components/Graph/model/GraphCommandContext';
import { diffGraphEdgeSettings } from 'components/Graph/model/GraphEdgeSettingsDiff';
import { getEdgeFromGroupByProtocol } from './helpers/getEdgeFromGroupByProtocol';
import { getLabelsVisibilityDiff } from './helpers/getLabelsVisibilityDiff';

export interface SetProtocolSettingsCommand {
  groupId: GroupEdgeSettings['id'] | null;
  protocolName?: ProtocolEdgeType;
  values: FormSettingsValues;
}

const makeIsGroup =
  (groupId: GroupEdgeSettings['id'] | null) =>
  (group: TopologyGraphEdgeSettings): group is GroupEdgeSettings =>
    group.type === 'group' && group.id === groupId;

export const setProtocolSettings: GraphCommandFactory<
  SetProtocolSettingsCommand
> = (cmdContext, dispatch) => (command) => {
  const currentEdge = getEdgeFromGroupByProtocol(
    cmdContext.getState(),
    command.groupId,
    command.protocolName,
  );
  const currentPresetDraft = cmdContext.getState().layoutSettings.presetDraft;
  const currentLabel =
    currentEdge !== undefined && isGroupSettings(currentEdge)
      ? currentEdge.label
      : null;

  const currentGroupToUpdate =
    currentEdge !== undefined && !isGroupSettings(currentEdge)
      ? currentPresetDraft.edges.find(makeIsGroup(command.groupId))
      : null;

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  cmdContext.updateState((draft) => {
    const edge = getEdgeFromGroupByProtocol(
      draft,
      command.groupId,
      command.protocolName,
    );
    if (!edge) {
      return;
    }
    edge.style.color = command.values.color;
    edge.style.pattern = command.values.pattern;
    edge.style.thicknessThresholds = [
      command.values.lineThicknessThin,
      command.values.lineThicknessMedium,
      command.values.lineThicknessThick,
    ];

    if (edge.type === 'group' && command.values.label) {
      edge.label = command.values.label;
    }

    if (edge.type === 'group' && command.values.name) {
      const foundGroup = draft.layoutSettings.presetDraft.edges.find(
        (group: GraphEdgeSettings): group is GroupEdgeSettings =>
          group.type === 'group' && group.id === command.groupId,
      );
      if (!foundGroup) {
        return;
      }
      foundGroup.name = command.values.name;
    }

    if (edge.type === 'protocol') {
      edge.labels = [
        ...command.values.labelCenter,
        ...command.values.labelLinecap,
      ];
    }

    edge.labelsOptions = {
      wrapCenterLabel: command.values.wrapCenterLabel,
      wrapLinecapLabel: command.values.wrapLinecapLabel,
    };
  });

  const updatedEdge = getEdgeFromGroupByProtocol(
    cmdContext.getState(),
    command.groupId,
    command.protocolName,
  );
  const updatedPresetDraft = cmdContext.getState().layoutSettings.presetDraft;

  const edgeSettingsLabelsVisibilityDiff =
    currentEdge &&
    updatedEdge &&
    getLabelsVisibilityDiff(
      currentEdge,
      updatedEdge,
      currentPresetDraft,
      updatedPresetDraft,
      command.groupId,
      command.protocolName,
    );

  dispatch(updateGraphDataFromSettings, {
    currentLabel,
    edgeSettingsDiff: diffGraphEdgeSettings(currentEdge, updatedEdge),
    edgeSettingsLabelsVisibilityDiff,
    groupId: command.groupId,
    protocolName: command.protocolName,
  });

  // Group has to be updated to pass label changes from protocol
  if (currentGroupToUpdate) {
    const updatedGroup = cmdContext
      .getState()
      .layoutSettings.presetDraft.edges.find(makeIsGroup(command.groupId));
    const groupSettingsLabelsVisibilityDiff =
      updatedGroup &&
      getLabelsVisibilityDiff(
        currentGroupToUpdate,
        updatedGroup,
        currentPresetDraft,
        updatedPresetDraft,
        command.groupId,
      );

    dispatch(updateGraphDataFromSettings, {
      currentLabel,
      edgeSettingsDiff: diffGraphEdgeSettings(
        currentGroupToUpdate,
        updatedGroup,
      ),
      edgeSettingsLabelsVisibilityDiff: groupSettingsLabelsVisibilityDiff,
      groupId: command.groupId,
    });
  }
};
